import { buildSearchParams, urlBuilder } from '@starjack/utils';
import { Language } from '@starjack/utils';
import { createEntityApi, type EntityEndpointBuilder } from '@starjack/api';
import {
    CreateFaq,
    CreateFaqGroup,
    Faq,
    FaqGroup,
    FaqGroupStructure,
} from '../model/faq.types';

export type GetFaqsResponse = {
    faqs: Faq[];
    groups: FaqGroup[];
};

const faqUrls = {
    faq: urlBuilder('order', `/faqs`),
    faqGroup: urlBuilder('order', `/faqs/groups`),
};

/**
 * Type definition for the API with injected FAQ hooks
 */
export interface FaqApiHooks {
    useGetFaqsQuery: (params?: {
        filtered?: boolean;
        liftOperatorId?: string;
    }) => {
        data?: FaqGroupStructure;
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    usePostFaqMutation: () => [
        (faq: CreateFaq) => { unwrap: () => Promise<Faq> },
        { isLoading: boolean; error?: unknown },
    ];
    usePutFaqMutation: () => [
        (faq: Faq) => { unwrap: () => Promise<Faq> },
        { isLoading: boolean; error?: unknown },
    ];
    useDeleteFaqMutation: () => [
        (params: { faqId: string }) => { unwrap: () => Promise<Faq> },
        { isLoading: boolean; error?: unknown },
    ];
    usePostFaqGroupMutation: () => [
        (faqGroup: CreateFaqGroup) => { unwrap: () => Promise<FaqGroup> },
        { isLoading: boolean; error?: unknown },
    ];
    usePutFaqGroupMutation: () => [
        (faqGroup: FaqGroup) => { unwrap: () => Promise<FaqGroup> },
        { isLoading: boolean; error?: unknown },
    ];
    useDeleteFaqGroupMutation: () => [
        (params: { faqGroupId: string }) => { unwrap: () => Promise<FaqGroup> },
        { isLoading: boolean; error?: unknown },
    ];
}

/**
 * FAQ endpoint definitions
 */
export const faqEndpoints = (builder: EntityEndpointBuilder) => ({
    getFaqs: builder.query<
        FaqGroupStructure,
        { filtered?: boolean; liftOperatorId?: string }
    >({
        // For migration: always request German FAQs from backend (language=DE)
        // while still allowing optional `filtered` flag.
        query: (params = {}) => {
            const searchParams = buildSearchParams({
                // TODO: MIGRATION - Remove hardcoded language=DE after migration is complete
                language: Language.DE,
                filtered: params.filtered,
                liftOperatorId: params.liftOperatorId,
            });
            return `${faqUrls.faq}${searchParams}`;
        },

        transformResponse(response: GetFaqsResponse) {
            const { faqs, groups } = response;

            const groupsWithFaqs = groups.map((group) => ({
                ...group,
                faqs: faqs.filter((faq) => faq.groupId === group.id),
            }));

            // add no group faqs
            const noGroupFaqs = faqs.filter((faq) => !faq.groupId);
            if (noGroupFaqs.length > 0) {
                groupsWithFaqs.unshift({
                    id: 'no-group',
                    name: {
                        de: 'Ohne Kategorie',
                        en: 'Without Category',
                    },
                    priority: 0,
                    faqs: noGroupFaqs,
                });
            }

            return groupsWithFaqs;
        },
        providesTags: ['FAQS'],
    }),

    postFaq: builder.mutation<Faq, CreateFaq>({
        query: (faq) => ({
            url: faqUrls.faq,
            method: 'POST',
            body: faq,
        }),
        invalidatesTags: ['FAQS'],
    }),

    putFaq: builder.mutation<Faq, Faq>({
        query: (faq) => ({
            url: `${faqUrls.faq}/${faq.id}`,
            method: 'PUT',
            body: faq,
        }),
        invalidatesTags: ['FAQS'],
    }),

    deleteFaq: builder.mutation<Faq, { faqId: string }>({
        query: ({ faqId }) => ({
            url: `${faqUrls.faq}/${faqId}`,
            method: 'DELETE',
            body: undefined,
        }),
        invalidatesTags: ['FAQS'],
    }),

    // groups
    postFaqGroup: builder.mutation<FaqGroup, CreateFaqGroup>({
        query: (faqGroup) => ({
            url: faqUrls.faqGroup,
            method: 'POST',
            body: faqGroup,
        }),
        invalidatesTags: ['FAQS'],
    }),

    putFaqGroup: builder.mutation<FaqGroup, FaqGroup>({
        query: (faqGroup) => ({
            url: `${faqUrls.faqGroup}/${faqGroup.id}`,
            method: 'PUT',
            body: faqGroup,
        }),
        invalidatesTags: ['FAQS'],
    }),

    deleteFaqGroup: builder.mutation<FaqGroup, { faqGroupId: string }>({
        query: ({ faqGroupId }) => ({
            url: `${faqUrls.faqGroup}/${faqGroupId}`,
            method: 'DELETE',
            body: undefined,
        }),
        invalidatesTags: ['FAQS'],
    }),
});

export const faqApi = createEntityApi({
    tagTypes: ['FAQS', 'FAQ_GROUPS'],
    endpoints: faqEndpoints,
});

// Export hooks with explicit types for TypeScript inference
export const useGetFaqsQuery: FaqApiHooks['useGetFaqsQuery'] =
    faqApi.useGetFaqsQuery;
export const usePostFaqMutation: FaqApiHooks['usePostFaqMutation'] =
    faqApi.usePostFaqMutation;
export const usePutFaqMutation: FaqApiHooks['usePutFaqMutation'] =
    faqApi.usePutFaqMutation;
export const useDeleteFaqMutation: FaqApiHooks['useDeleteFaqMutation'] =
    faqApi.useDeleteFaqMutation;
export const usePostFaqGroupMutation: FaqApiHooks['usePostFaqGroupMutation'] =
    faqApi.usePostFaqGroupMutation;
export const usePutFaqGroupMutation: FaqApiHooks['usePutFaqGroupMutation'] =
    faqApi.usePutFaqGroupMutation;
export const useDeleteFaqGroupMutation: FaqApiHooks['useDeleteFaqGroupMutation'] =
    faqApi.useDeleteFaqGroupMutation;
